import { Capacitor } from "@capacitor/core";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";
import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import { getCheckoutStripeRedirect } from "@/modules/checkout";
import { PromoCodeErrorModal } from "@/pages/root/cart/PromoCodeErrorModal";
import { formatToLongDate } from "@/utils/dayjsUtils";
import { get, post } from "api";
import { Models, ShopDisplayType } from "api/types";
import {
  AFFIRM_MINIMUM_AMOUNT,
  AffirmUnavailableReason,
  NotAvailableAffirm,
} from "atoms/affirm/NotAvailableAffirm/NotAvailableAffirm";
import { Button } from "atoms/button/Button";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { Icon } from "atoms/icon/Icon";
import Warning from "atoms/icon/icons/ic_warning_ol.svg";
import { Modal } from "atoms/modal/Modal";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { TextField } from "atoms/text-field/TextField";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { OrderSummary } from "organisms/order-summary/OrderSummary";
import { queryClient } from "pages/Root";
import { removeArchiveItems } from "utils/functions";
import {
  isRequiredValid,
  isValidDateFormat,
  isValidEmail,
} from "utils/validators";

type CheckoutFormProps = {
  platform: Models<"PlatformModel">;
  isGiftCardCreatedUser?: boolean;
  cartDetails: Models<"ShopDisplayOrderResult"> | null;
  transactionDetails: Models<"InitiateOrderTransactionResult"> | null;
  user: Models<"UserModel1"> | null;
  refreshUrl?: string;
  redirectBaseUrl?: string;
  isGiftCardLayout?: boolean;
};

export const CheckoutForm = ({
  cartDetails,
  transactionDetails,
  user,
  platform,
  refreshUrl = "/cart",
  redirectBaseUrl = "/cart/checkout/success",
  isGiftCardLayout = false,
}: CheckoutFormProps) => {
  const stripeKey = useStripePublishableKey();

  const stripePromise = stripeLoader.getStripe(stripeKey);

  if (!transactionDetails || !platform) {
    return null;
  }

  if (!transactionDetails.clientSecret) {
    // Client secret is only null for free purchases
    throw new Error("Client secret is required for checkout");
  }

  return (
    <div className="CheckoutForm h-full">
      <Elements
        stripe={stripePromise}
        options={
          {
            onBehalfOf:
              platform.paymentProcessor?.paymentProcessorIdentifier ??
              undefined,
            clientSecret: transactionDetails.clientSecret,
          } as StripeElementsOptions
        }
        key={transactionDetails.paymentIntentId}
      >
        <StripeCheckoutForm
          orderTransactionId={transactionDetails.orderTransactionId}
          cartDetails={cartDetails as Models<"ShopDisplayOrderResult">}
          platform={platform}
          user={user as Models<"UserModel1">}
          transactionDetails={transactionDetails}
          refreshUrl={refreshUrl}
          redirectBaseUrl={redirectBaseUrl}
          isGiftCardLayout={isGiftCardLayout}
        />
      </Elements>
    </div>
  );
};

type StripeCheckoutFormProps = {
  orderTransactionId: string;
  cartDetails: Models<"ShopDisplayOrderResult">;
  platform: Models<"PlatformModel">;
  user: Models<"UserModel1">;
  transactionDetails: Models<"InitiateOrderTransactionResult">;
  refreshUrl: string;
  redirectBaseUrl: string;
  isGiftCardLayout?: boolean;
};

type CompleteProfileFormErrorStates = {
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  dateOfBirth?: boolean;
  allowedMarketingCampaigns?: boolean;
};

const StripeCheckoutForm = ({
  orderTransactionId,
  cartDetails,
  platform,
  user,
  transactionDetails,
  refreshUrl,
  redirectBaseUrl,
  isGiftCardLayout = false,
}: StripeCheckoutFormProps) => {
  const { data: authClaims } = useQuery({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshModal, setRefreshModal] = useState(false);
  const [promoCodeError, setPromoCodeErrorModal] = useState("");
  const [isStripeFormReady, setIsStripeFormReady] = useState(false);
  const [redirectLink, setRedirectLink] = useState<{
    internal: string;
    external: string;
  }>({ internal: "", external: "" });

  const containsShipableProducts =
    cartDetails?.shopDisplayOrderLineItems?.find(
      (item) => item.displayType === ShopDisplayType.ProductFromExternalSource
    ) != null;

  // Complete profile form states
  const [completeProfileFormData, setCompleteProfileFormData] = useState({
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    dateOfBirth: user.dateOfBirth ?? "",
    email: user.email ?? "",
    allowedMarketingCampaigns: false,
    defaultEmail: true,
  });
  const [completeProfileFormErrors, setCompleteProfileFormErrors] =
    useState<CompleteProfileFormErrorStates>({});
  const [dob, setDob] = useState(
    user?.dateOfBirth ? dayjs(user.dateOfBirth).format("L") : ""
  );

  const {
    data: transaction,
    fetchStatus: transactionQueryStatus,
    refetch: fetchTransaction,
  } = useQuery({
    queryKey: [
      "/orders/transaction/{shopDisplayOrderTransactionId}",
      {
        shopDisplayOrderTransactionId: orderTransactionId,
        checkExpired: false,
      },
    ],
    queryFn: () =>
      get("/orders/transaction/{shopDisplayOrderTransactionId}", {
        shopDisplayOrderTransactionId: orderTransactionId,
        checkExpired: false,
      }),
    enabled: Capacitor.isNativePlatform(),
    refetchOnWindowFocus: "always",
  });

  // This ref is used to bail out of the stripe submission flow if the status of
  // the underlying payment intent changes
  const cancelSubmitRef = useRef(false);
  useEffect(() => {
    if (!transaction) {
      return;
    }

    const status =
      transaction.paymentIntentStatus ?? transaction.setupIntentStatus;
    if (status === "RequiresPaymentMethod") {
      return;
    }

    cancelSubmitRef.current = true;
    navigate(redirectLink.internal);
  }, [transaction]);

  const isCompletingProfileWhileCheckingOut =
    !user.completedProfile && user.hasBeenPreRegistered;

  const validateCompleteProfileForm = () => {
    const errors = {
      ...(isCompletingProfileWhileCheckingOut
        ? {
            firstName: !isRequiredValid(completeProfileFormData.firstName),
            lastName: !isRequiredValid(completeProfileFormData.lastName),
            dateOfBirth: !!dob && (dob.length < 10 || !isValidDateFormat(dob)),
            allowedMarketingCampaigns:
              !completeProfileFormData.allowedMarketingCampaigns,
          }
        : {}),
      email: !isValidEmail(completeProfileFormData.email),
    };

    setCompleteProfileFormErrors(errors);

    return Object.values(errors).some((error) => error);
  };

  const { mutate: handleSubmit, status: submitStatus } = useMutation({
    mutationFn: async () => {
      const doesCompleteProfileHasErrors = validateCompleteProfileForm();

      if (
        doesCompleteProfileHasErrors ||
        !stripe ||
        !elements ||
        !isStripeFormReady
      ) {
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        return;
      }

      const details = await queryClient.ensureQueryData({
        queryKey: CURRENT_ORDER_QUERY_KEY,
        queryFn: () => get("/orders/current/details").catch(() => null),
        staleTime: 5 * 60 * 1000,
      });

      if (!details) {
        return;
      }

      const isCartEmpty = !details.shopDisplayOrderLineItems?.length;
      const archivedRemoved = await removeArchiveItems(details);

      if (isCartEmpty || archivedRemoved !== 0) {
        navigate("/cart");
        return;
      }

      const promoCodeError = details.discountSummary?.promoCodeError;
      if (promoCodeError) {
        setPromoCodeErrorModal(promoCodeError);
        return;
      }

      let updateUserProfile;
      // If the user is completing their profile while checking out (custom plan user), we need to
      // update their profile information before proceeding
      if (isCompletingProfileWhileCheckingOut) {
        updateUserProfile = post("/user/update", {
          firstName: completeProfileFormData.firstName,
          lastName: completeProfileFormData.lastName,
          email: completeProfileFormData.email,
          dateOfBirth: completeProfileFormData.dateOfBirth,
        });
      } else if (
        !completeProfileFormData.defaultEmail &&
        completeProfileFormData.email != user.email
      ) {
        // otherwise we only need to update their email if they've changed it
        updateUserProfile = post("/user/update", {
          email: completeProfileFormData.email,
        });
      } else {
        updateUserProfile = Promise.resolve();
      }

      const prepare = post(
        "/orders/current/transactions/{transactionId}/prepare",
        { transactionId: orderTransactionId }
      );

      if (isCompletingProfileWhileCheckingOut) {
        await queryClient.invalidateQueries({ queryKey: ["/user"] });
        await queryClient.refetchQueries({ queryKey: ["/user"] });
      }

      await Promise.all([updateUserProfile, prepare]);

      cancelSubmitRef.current = false;
      const cancellation = new Promise<never>((_, rej) => {
        const poll = () => {
          if (cancelSubmitRef.current) {
            rej();
          } else {
            setTimeout(poll);
          }
        };
        setTimeout(poll);
      });

      // Use confirmSetup for free transactions that include a membership (SetupIntent)
      // or confirmPayment for regular transactions (PaymentIntent)
      const confirmation = transactionDetails?.setupIntentId
        ? stripe.confirmSetup({
            elements,
            confirmParams: { return_url: redirectLink.external },
            redirect: "if_required",
          })
        : stripe.confirmPayment({
            elements,
            confirmParams: { return_url: redirectLink.external },
            redirect: "if_required",
          });

      const { error } = await Promise.race([confirmation, cancellation]);
      cancelSubmitRef.current = true;

      if (!error) {
        return navigate(redirectLink.internal);
      }

      if (error.message) {
        setErrorMessage(error.message);
      }

      // Check again to see if the payment intent status has actually advanced
      const { data: transaction } = await fetchTransaction();
      const status =
        transaction?.paymentIntentStatus ?? transaction?.setupIntentStatus;
      if (status !== undefined && status !== "RequiresPaymentMethod") {
        return navigate(redirectLink.internal);
      }

      let resetUserProfile;
      // If the user is completing their profile while checking out (custom plan user), we need to
      // reset their profile information
      if (isCompletingProfileWhileCheckingOut) {
        resetUserProfile = post("/user/update", {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          dateOfBirth: user.dateOfBirth,
        });
      } else if (
        !completeProfileFormData.defaultEmail &&
        completeProfileFormData.email != user.email
      ) {
        // otherwise we just reset their email if they've changed it
        resetUserProfile = post("/user/update", { email: user.email });
      } else {
        resetUserProfile = Promise.resolve();
      }

      const unprepare = post(
        "/orders/current/transactions/{transactionId}/unprepare",
        { transactionId: orderTransactionId }
      );

      await Promise.all([resetUserProfile, unprepare]);
    },
    onError: (error) => {
      console.error(error);
      setRefreshModal(true);
    },
  });

  useEffect(() => {
    // We want to avoid showing the refresh modal if the Stripe form is ready, or
    // if the submission is in progress
    if (isStripeFormReady || submitStatus === "loading") {
      return;
    }

    const timeoutId = setTimeout(() => {
      setRefreshModal(true);
    }, 30_000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isStripeFormReady, submitStatus]);

  useEffect(() => {
    const fetchRedirectLink = async () => {
      if (cartDetails && platform) {
        const redirectData = await getCheckoutStripeRedirect(
          platform.id,
          cartDetails.shopDisplayOrderId,
          orderTransactionId,
          redirectBaseUrl
        );
        setRedirectLink(redirectData);
      }
    };

    fetchRedirectLink();
  }, [cartDetails, platform, orderTransactionId]);

  if (!cartDetails) return null;

  const containsMembership =
    cartDetails?.shopDisplayOrderLineItems?.find(
      (item) => item.shopDisplayMembershipId != null
    ) != null;
  const containsPackageOrTreatment =
    cartDetails?.shopDisplayOrderLineItems?.find(
      (item) => item.shopDisplayPackageId != null
    ) != null;

  const containsGiftCard = cartDetails?.shopDisplayOrderLineItems?.some(
    (item) => item.displayType === ShopDisplayType.GiftCard
  );

  const isOrderTotalAboveMinimum =
    cartDetails.estimatedTotal / 100 > AFFIRM_MINIMUM_AMOUNT;

  const closePromoCodeErrorModal = () => {
    setPromoCodeErrorModal("");
    window.location.reload();
  };

  return (
    <div className="StripeCheckoutForm">
      <div className="flex flex-col border-b border-b-light-grey pb-5 pt-6">
        <div className="flex flex-col px-5">
          <div className="text-bold1">Contact details</div>
          <div className="pb-4 pt-2 text-body2 text-secondary">
            {isCompletingProfileWhileCheckingOut
              ? `Add your information to create your ${platform.name} account and place your order`
              : "Confirm your details to receive your sales receipt"}
          </div>
          <div className="flex flex-col gap-3">
            {isCompletingProfileWhileCheckingOut && (
              <>
                <div className="flex gap-3">
                  <TextField
                    type="text"
                    label="First Name"
                    name="firstName"
                    rightIcon={
                      completeProfileFormErrors.firstName ? Warning : undefined
                    }
                    supportingText={
                      completeProfileFormErrors.firstName
                        ? "Field is required"
                        : undefined
                    }
                    invalid={!!completeProfileFormErrors.firstName}
                    value={completeProfileFormData.firstName}
                    onChange={(value) => {
                      setCompleteProfileFormData((formData) => ({
                        ...formData,
                        firstName: value,
                      }));
                      setCompleteProfileFormErrors((errors) => ({
                        ...errors,
                        firstName: false,
                      }));
                    }}
                  />
                  <TextField
                    type="text"
                    label="Last Name"
                    name="lastName"
                    rightIcon={
                      completeProfileFormErrors.lastName ? Warning : undefined
                    }
                    supportingText={
                      completeProfileFormErrors.lastName
                        ? "Field is required"
                        : undefined
                    }
                    value={completeProfileFormData.lastName}
                    invalid={!!completeProfileFormErrors.lastName}
                    onChange={(value) => {
                      setCompleteProfileFormData((formData) => ({
                        ...formData,
                        lastName: value,
                      }));
                      setCompleteProfileFormErrors((errors) => ({
                        ...errors,
                        lastName: false,
                      }));
                    }}
                  />
                </div>
                <TextField
                  type="text"
                  label="Phone Number"
                  name="phoneNumber"
                  value={user.phoneNumber}
                  disabled
                />
              </>
            )}
            <TextField
              type="email"
              label="Email"
              name="email"
              rightIcon={completeProfileFormErrors.email ? Warning : undefined}
              placeholder="Confirm your email address"
              supportingText={
                completeProfileFormErrors.email
                  ? "Please enter a valid email address"
                  : undefined
              }
              value={completeProfileFormData.email}
              onChange={(value) => {
                setCompleteProfileFormData((formData) => ({
                  ...formData,
                  email: value,
                }));
                setCompleteProfileFormErrors((errors) => ({
                  ...errors,
                  email: false,
                }));
              }}
              invalid={!!completeProfileFormErrors.email}
            />
            {isCompletingProfileWhileCheckingOut && (
              <TextField
                type="decimal"
                label="Date of birth (optional)"
                name="dateOfBirth"
                rightIcon={
                  completeProfileFormErrors.dateOfBirth ? Warning : undefined
                }
                supportingText={
                  completeProfileFormErrors.dateOfBirth
                    ? `Please enter a valid date in the format ${dayjs
                        .localeData()
                        .longDateFormat("L")}`
                    : undefined
                }
                value={dob}
                maxLength={10}
                placeholder={dayjs.localeData().longDateFormat("L")}
                pattern="[0-9]*"
                invalid={!!completeProfileFormErrors.dateOfBirth}
                onChange={(value: string) => {
                  const formatted = formatToLongDate(value);
                  setDob(formatted);
                  if (formatted.length >= 10 && isValidDateFormat(formatted)) {
                    setCompleteProfileFormData((formData) => ({
                      ...formData,
                      dateOfBirth: new Date(formatted).toISOString(),
                    }));
                    setCompleteProfileFormErrors((errors) => ({
                      ...errors,
                      dateOfBirth: false,
                    }));
                  }
                }}
              />
            )}
          </div>
        </div>
        <div className="pl-2.5 pr-5 pt-3">
          {isCompletingProfileWhileCheckingOut ? (
            <Checkbox
              label="I agree to receive special rewards, gifts and promotional material via text messages from MyRepeat"
              checked={completeProfileFormData.allowedMarketingCampaigns}
              onChange={(checked: boolean) => {
                setCompleteProfileFormData({
                  ...completeProfileFormData,
                  allowedMarketingCampaigns: checked,
                });
                setCompleteProfileFormErrors((errors) => ({
                  ...errors,
                  allowedMarketingCampaigns: false,
                }));
              }}
              invalid={!!completeProfileFormErrors.allowedMarketingCampaigns}
            />
          ) : (
            <Checkbox
              label="Save address to your account"
              checked={completeProfileFormData.defaultEmail}
              onChange={(checked: boolean) => {
                setCompleteProfileFormData({
                  ...completeProfileFormData,
                  defaultEmail: checked,
                });
                setCompleteProfileFormErrors((errors) => ({
                  ...errors,
                  defaultEmail: false,
                }));
              }}
            />
          )}
        </div>
      </div>

      {isAdminImpersonation && (
        <div className="mx-4 mt-4 rounded-lg bg-yellow-600/90 px-6 py-4 shadow-sm">
          <div className="flex items-center gap-3">
            <Icon svg={Warning} size="small" color="white" />
            <span className="text-bold2 text-white">
              Caution: You are using the app as {user.firstName}. Double check
              that you are not using an auto-filled payment card.
            </span>
          </div>
        </div>
      )}

      {containsShipableProducts && (
        <>
          <div className="pl-5 pt-6 text-bold1">Shipping Address</div>
          <div className="flex w-full flex-col">
            <div className="px-5 pb-5 pt-4">
              <AddressElement
                options={{
                  mode: "shipping",
                  autocomplete: { mode: "automatic" },
                  allowedCountries: ["US"],
                  defaultValues: {
                    name: `${user.firstName ?? ""} ${
                      user.lastName ?? ""
                    }`.trim(),
                    address: {
                      country: "US",
                    },
                  },
                }}
              ></AddressElement>
            </div>
          </div>
          <hr className=" bg-light-grey" />
        </>
      )}

      <div className="pl-5 pt-6 text-bold1">Payment method</div>
      {platform.platformSettings?.affirmEnabled &&
        (!isOrderTotalAboveMinimum ||
          (containsMembership && containsPackageOrTreatment) ||
          containsGiftCard) && (
          <div className="m-3">
            <NotAvailableAffirm
              isLargeBanner={false}
              reason={
                containsGiftCard
                  ? AffirmUnavailableReason.ContainsGiftCard
                  : containsMembership && containsPackageOrTreatment
                  ? AffirmUnavailableReason.ContainsMembership
                  : AffirmUnavailableReason.BelowMinimum
              }
            />
          </div>
        )}

      <div className="flex w-full flex-col">
        <div className="px-5 pb-5 pt-4">
          <PaymentElement
            onReady={() => setIsStripeFormReady(true)}
            onLoadError={({ error }) => {
              setIsStripeFormReady(false);
              setRefreshModal(true);

              console.error("Stripe PaymentElement load error:", error);
            }}
            options={{
              ...(containsMembership && {
                applePay: {
                  recurringPaymentRequest: {
                    paymentDescription: `${platform.name} subscription`,
                    managementURL: `https://${platform.host}/wallet/memberships/manage`,
                    regularBilling: {
                      amount: (
                        cartDetails.shopDisplayOrderLineItems?.filter(
                          (item) => item.displayType === 2
                        ) ?? []
                      ).reduce((sum, item) => sum + item.cost, 0),
                      label:
                        "Memberships in this purchase will renew monthly. You can cancel by contacting the office directly.",
                      recurringPaymentIntervalCount: 1,
                      recurringPaymentIntervalUnit: "month",
                    },
                  },
                },
              }),
            }}
          />
        </div>
        <hr className=" bg-light-grey" />
        <div className="px-5 pb-5 pt-5">
          {cartDetails && (
            <>
              <OrderSummary cart={cartDetails} displayProducts />
              {promoCodeError && (
                <PromoCodeErrorModal
                  onClose={closePromoCodeErrorModal}
                  error={promoCodeError}
                />
              )}
            </>
          )}
          <RepeatMDFooter showShopTerms />
        </div>
        <div
          className={cn(
            "sticky bottom-0 w-full bg-white p-5 pb-[calc(20px_+_env(safe-area-inset-bottom))]",
            isGiftCardLayout && "lg:-bottom-6"
          )}
        >
          <Button
            onClick={() => handleSubmit()}
            disabled={
              !stripe ||
              !elements ||
              !isStripeFormReady ||
              submitStatus === "loading" ||
              transactionQueryStatus === "fetching" ||
              refreshModal
            }
            fullWidth
          >
            Place order
          </Button>
          {errorMessage && (
            <div className="flex max-w-full flex-col items-center hyphens-auto text-wrap text-body1 text-primary">
              {errorMessage}
            </div>
          )}
        </div>
      </div>

      <Modal open={refreshModal}>
        <div className="flex flex-col items-center gap-4 p-8">
          <Icon svg={Warning} size="large" />
          <div className="text-center text-body1 text-primary">
            Your checkout session has expired. Please refresh to continue.
          </div>
          <Button onClick={() => navigate(refreshUrl)} fullWidth>
            Refresh
          </Button>
        </div>
      </Modal>
    </div>
  );
};
